import { motion } from "framer-motion";
import { Wrench, Users, Buildings, ChartLineUp } from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { Eyebrow, BookDemoButton, LinkButton } from "@/components/marketing/Primitives";
import { timeline } from "@/lib/content";

const ABOUT_IMG = "https://images.unsplash.com/photo-1621905251918-48416bd8575a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const chapters = [
  { n: "01", title: "It started on site, not in an office", text: "FlowCore wasn't created by a software company. It was created by engineers who were tired of clunky tools that never understood field service." },
  { n: "02", title: "Built from real operational experience", text: "Every feature has been designed from real-world experience — the messy jobs, the missed services, the lost stock and the paperwork that never ended." },
  { n: "03", title: "One platform to run it all", text: "Today FlowCore helps businesses manage engineers, servicing, installations, customers, assets and operations from one cloud platform." },
];

const stats = [
  { icon: Users, value: 5000, suffix: "+", label: "Engineers empowered" },
  { icon: Buildings, value: 11, suffix: "", label: "Industries served" },
  { icon: Wrench, value: 1, prefix: "", suffix: "M+", label: "Jobs completed" },
  { icon: ChartLineUp, value: 32, suffix: "%", label: "Less admin on average" },
];

export default function About() {
  return (
    <>
      <SEO
        title="About FlowCore — Built by Engineers, for Field Service"
        description="FlowCore wasn't created by a software company — it was created by engineers who wanted a better way to run field service businesses. Discover our story."
      />
      <section className="relative overflow-hidden pt-36 pb-16">
        <div className="pointer-events-none absolute inset-0 radial-fade" />
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <Eyebrow className="mx-auto">Our story</Eyebrow>
            <h1 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Built by engineers, for engineers
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-muted-foreground md:text-lg">
              We know field service because we've lived it. FlowCore exists to give operators the platform we always wished we had.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <div className="pointer-events-none absolute -inset-4 bg-primary/10 blur-3xl" />
              <img src={ABOUT_IMG} alt="Field engineer at work" className="relative aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <div className="space-y-10">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="flex gap-5">
                  <span className="font-heading text-3xl font-extrabold text-primary/40">{c.n}</span>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-white">{c.title}</h3>
                    <p className="mt-2 leading-relaxed text-muted-foreground">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Stagger className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {stats.map((s) => {
              const Icon = s.icon;
              return (
                <StaggerItem key={s.label}>
                  <div className="rounded-2xl border border-white/10 bg-card p-7 text-center">
                    <Icon size={30} weight="duotone" className="mx-auto text-primary" />
                    <p className="mt-3 font-heading text-3xl font-extrabold text-white sm:text-4xl">
                      <Counter value={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} />
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground">{s.label}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Milestones</Eyebrow>
            <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">The FlowCore journey</h2>
          </Reveal>
          <div className="relative mt-12 space-y-8 border-l border-white/10 pl-8">
            {timeline.map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="relative">
                  <motion.span
                    className="absolute -left-[41px] top-1 flex h-5 w-5 items-center justify-center rounded-full border border-primary/40 bg-background"
                    whileInView={{ scale: [0.6, 1] }}
                    viewport={{ once: true }}
                  >
                    <span className="h-2 w-2 rounded-full bg-primary" />
                  </motion.span>
                  <h3 className="font-heading text-lg font-semibold text-primary">{t.year}</h3>
                  <p className="mt-1 text-muted-foreground">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 text-center">
        <div className="mx-auto max-w-3xl px-5">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">Come and see what we've built</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">Book a demo and experience the platform engineers designed for the field.</p>
          <div className="mt-8 flex justify-center gap-4">
            <BookDemoButton testid="about-cta-demo" />
            <LinkButton to="/features" testid="about-cta-features">Explore Features</LinkButton>
          </div>
        </div>
      </section>
    </>
  );
}
